import React from "react";
import {
  Button,
  Form,
  Card,
  CardTitle,
  CardBody,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  Pagination,
  PaginationOptions,
} from "react-bs-datatable";
import ExportDialog from "../Export/ExportDialog";

const Datatable = ({
  data,
  headers,
  addButtonText,
  addButtonLink = "/add",
  // Given instead of a link, the add button calls this rather than
  // navigating -- for a page that opens its form in a dialog and stays
  // where it is. addButtonLink is ignored when it is set.
  onAddClick,
  pageTitle = "Data",
  showExportButton = true,
  exportData,
  // --- Server-side pagination (all optional — omit for the existing
  // client-side behavior every other page using this component keeps).
  // When `serverSide` is true, `data` is expected to be just the CURRENT
  // page's rows (already filtered/paginated by the backend); this
  // component stops filtering/paginating locally and instead renders
  // `data` as-is, driving react-bs-datatable's Pagination/PaginationOptions
  // in "controlled" mode (see their `controlledProps`) so the page-size
  // and page-number controls call back out to `onPageChange`/
  // `onRowsPerPageChange` instead of re-slicing an in-memory array.
  serverSide = false,
  totalRecords = 0,
  currentPage = 1,
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 20],
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedField, setSelectedField] = React.useState("all");
  const [exportOpen, setExportOpen] = React.useState(false);

  const searchableFields = headers
    .filter((header) => header.prop !== "action")
    .map((header) => ({ title: header.title, prop: header.prop }));

  const dataArray = Array.isArray(data) ? data : data?.data || [];

  // In server-side mode `dataArray` is already exactly what should be
  // shown — the backend already applied whatever filtering the page's own
  // filter bar represents. This component's own free-text search box
  // (below) is hidden in that mode too, so there's nothing left to filter
  // client-side here.
  const filteredData = serverSide
    ? dataArray
    : dataArray.filter((row) => {
        if (!searchTerm) return true;
        if (selectedField === "all") {
          return Object.values(row).some((value) =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
          );
        } else {
          return String(row[selectedField] || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }
      });

  const maxPage = serverSide ? Math.max(1, Math.ceil(totalRecords / Math.max(1, rowsPerPage))) : undefined;

  /*
   * The columns, in the shape exportRows.js wants. This page's copy of
   * the CSV writer went with the popup: it quoted on comma and quote
   * only -- a value holding a newline shifted every column after it
   * onto a row of its own -- and wrote no BOM, so Excel read a name
   * with an accent in it as the system codepage.
   */
  const exportColumns = headers
    .filter((header) => header.prop !== "action")
    .map((header, i) => ({
      key: header.prop || `col-${i}`,
      label: header.title,
      value: (row) => row[header.prop],
    }));

  const exportScopeNote = serverSide
    ? totalRecords > filteredData.length
      ? `Page ${currentPage} of ${maxPage} — the other ${(
          totalRecords - filteredData.length
        ).toLocaleString()} rows are on the server and are not in this file.`
      : undefined
    : searchTerm
      ? `Filtered by “${searchTerm}” in ${
          selectedField === "all"
            ? "all fields"
            : searchableFields.find((f) => f.prop === selectedField)?.title ||
              selectedField
        }.`
      : undefined;

  return (
    <Card className="">
      <CardTitle></CardTitle>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            {/* This component's own free-text search is client-side over
                whatever `data` it was handed — meaningless (and misleading)
                in server-side mode, where `data` is just the current page
                and real filtering already happened on the backend. */}
            {!serverSide && (
              <>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: "300px" }}
                />
                <Form.Select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  style={{ maxWidth: "180px" }}
                >
                  <option value="all">All Fields</option>
                  {searchableFields.map((field) => (
                    <option key={field.prop} value={field.prop}>
                      {field.title}
                    </option>
                  ))}
                </Form.Select>
              </>
            )}
          </div>

          <div className="d-flex gap-2">
            {showExportButton && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-export">
                    {serverSide
                      ? `Export this page (${filteredData.length} of ${totalRecords} records)`
                      : searchTerm
                      ? `Export filtered data (${filteredData.length} records)`
                      : `Export all data (${dataArray.length} records)`}
                  </Tooltip>
                }
              >
                <Button
                  variant="success"
                  aria-haspopup={exportData ? undefined : "dialog"}
                  aria-expanded={exportData ? undefined : exportOpen}
                  onClick={() => {
                    // A page that builds its own export still wins.
                    if (exportData && typeof exportData === "function") {
                      exportData(filteredData);
                    } else {
                      setExportOpen(true);
                    }
                  }}
                >
                  <i className="fa fa-download me-1"></i>
                  Export {searchTerm && `(${filteredData.length})`}
                </Button>
              </OverlayTrigger>
            )}

            {addButtonText && (
              <Button
                variant="primary"
                onClick={onAddClick ? onAddClick : () => navigate(addButtonLink)}
              >
                <i className="fa fa-plus me-1"></i> {addButtonText}
              </Button>
            )}
          </div>
        </div>

        <DatatableWrapper
          body={filteredData}
          headers={headers}
          paginationOptionsProps={
            serverSide
              ? undefined
              : {
                  initialState: {
                    rowsPerPage: 5,
                    options: [5, 10, 20],
                  },
                }
          }
        >
          <div className="table-responsive">
            <table className="table">
              <TableHeader />
              <TableBody />
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            {serverSide ? (
              <>
                <PaginationOptions
                  controlledProps={{
                    rowsPerPage,
                    rowsPerPageOptions,
                    filteredDataLength: totalRecords,
                    onRowsPerPageChange: (nextRowsPerPage) => onRowsPerPageChange?.(nextRowsPerPage),
                  }}
                />
                <Pagination
                  controlledProps={{
                    currentPage,
                    maxPage,
                    onPaginationChange: (nextPage) => onPageChange?.(nextPage),
                  }}
                />
              </>
            ) : (
              <>
                <PaginationOptions />
                <Pagination />
              </>
            )}
          </div>
        </DatatableWrapper>
      </CardBody>

      {/* Portalled to <body>, so a <Card> with its own stacking context
          around it makes no difference to where the dialog lands. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={pageTitle}
        columns={exportColumns}
        rows={filteredData}
        scopeNote={exportScopeNote}
        filenameBase={pageTitle}
      />
    </Card>
  );
};

export default Datatable;
